import { isPlainObject } from '@spazious/dollhouse-utils';
import {
  all,
  create,
  FunctionNode,
  MathJsInstance,
  MathNode,
  OperatorNode,
  ParenthesisNode,
  SymbolNode,
} from '@spazious/mathjs';

export { isResultSet } from '@spazious/mathjs';

export const math = create(all);

const createIf = Object.assign(
  (args: MathNode[], _: MathJsInstance, scope?: any) => {
    const [condition, valueExpr, fallbackExpr] = args;
    if (condition.compile().evaluate(scope)) {
      return valueExpr.compile().evaluate(scope);
    } else {
      return fallbackExpr.compile().evaluate(scope);
    }
  },
  {
    rawArgs: true,
  }
);

math.import({
  if: createIf,
  clamp: (value: number, min: number, max: number) => {
    return Math.max(min, Math.min(max, value));
  },
  fmax: (...values: number[]) => {
    return Math.max(...values);
  },
  fmin: (...values: number[]) => {
    return Math.min(...values);
  },
});

export const formatPath = (name: string, nested: string) =>
  `${name}.${nested}`.replace(/^root\./, '$.');

export const formatVar = (key: string) => key.replace(/^\$/, '');

export const parse = (expr: string) => math.parse(expr);

export const isSymbolNode = (node: MathNode): node is SymbolNode => {
  return node.type === 'SymbolNode';
};

export const isOperatorNode = (node: MathNode): node is OperatorNode => {
  return node.type === 'OperatorNode';
};

export const isFunctionNode = (node: MathNode): node is FunctionNode => {
  return node.type === 'FunctionNode';
};

export const isParenthesisNode = (node: MathNode): node is ParenthesisNode => {
  return node.type === 'ParenthesisNode';
};

export const traverseMathNode = (node: MathNode, cb: (node: MathNode) => void) => {
  cb(node);

  switch (node.type) {
    case 'ParenthesisNode':
      traverseMathNode((node as ParenthesisNode).content, cb);
      break;
    case 'OperatorNode':
      (node as OperatorNode).args.forEach((node: any) => {
        traverseMathNode(node, cb);
      });
      break;
  }
};

/**
 * Gets a value from a map Recursively.
 *
 * @example
 *```js
 * const map = new Map([
 *  ['foo', new Map([
 *   ['bar', 'baz']
 * ])]
 *
 * getMapValue(map, 'foo.bar') // 'baz'
 * ```
 */
export const getValue = (value: any, key: string): undefined | any => {
  return key.split('.').reduce((value, key) => {
    if (value instanceof Map) {
      return value.get(key);
    } else if (isPlainObject(value)) {
      return value[key];
    } else {
      return undefined;
    }
  }, value);
};

/**
 * Checks if a map has a value for the given key.
 *
 * @example
 * ```js
 * const map = new Map([
 * ['foo', new Map([
 *  ['bar', 'baz']
 * ])]
 *
 * hasMapValue(map, 'foo.bar') // true
 * hasMapValue(map, 'foo.baz') // false
 * ```
 *
 */
export const hasValue = (value: any, key: string): boolean => {
  return key.split('.').every((key) => {
    if (value instanceof Map && value.has(key)) {
      value = value.get(key);
      return true;
    }

    return false;
  });
};

export const getVariablesFromNode = (node: MathNode): string[] => {
  if (isSymbolNode(node)) {
    return [node.name];
  } else if (isOperatorNode(node) || isFunctionNode(node)) {
    return node.args.flatMap(getVariablesFromNode);
  } else if (isParenthesisNode(node)) {
    return getVariablesFromNode(node.content);
  } else {
    return [];
  }
};

export function extractDependencies(node: MathNode, scope: Map<string, number>) {
  const map = new Map<string, { expr: string } | number>(scope as any);

  const vars = new Set<string>();

  getVariablesFromNode(node).forEach((variable) => {
    if (map.has(variable)) {
      vars.add(variable);
      const value = map.get(variable);
      const expr = typeof value === 'object' && typeof value.expr === 'string' ? value.expr : value;
      if (typeof expr === 'string') {
        getVariablesFromNode(parse(expr)).forEach((variable) => {
          vars.add(variable);
        });
      }
    } else {
      throw new Error(
        `Variable "${variable}" is not defined in the scope \nExpression: "${node.toString()}"\nScope: ${JSON.stringify(
          Object.fromEntries(scope),
          null,
          2
        )}`
      );
    }
  });

  return vars;
}
